/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import axios from 'axios';
import {
  createStore,
  del,
  entries,
  values,
  get,
  set,
  setMany,
} from 'idb-keyval';

import Vue from 'vue';
import Vuex from 'vuex';

Vue.use(Vuex);

async function getJwt() {
  const accessStore = createStore('fieldToolAccess', 'access');
  let jwt;
  await get('access_token', accessStore).then((token) => {
    if (token) {
      jwt = token;
    }
  });
  return jwt;
}

function getRandomNegativeIdForAdHocComm(excludeIds) {
  const min = -9999;
  const max = -1;
  let id = Math.floor(Math.random() * (max - min + 1) + min);

  while (excludeIds.includes(Math.abs(id))) {
    id = Math.floor(Math.random() * (max - min + 1) + min);
  }
  return id;
}

export default new Vuex.Store({
  state: {
    config: {},
    configLoading: false,
    configLoaded: false,
    uploading: false,
    loggedIn: false,
    userLoggedIn: '',
    assessmentCategories: [],
    assessmentModel: {},
    categoriesLoading: false,
    fireDepartments: [],
    fireDepartmentsLoading: [],
    currentCommunity: { assessment: {} },
    downloadedCommunities: [],
    communities: [],
    communitiesLoading: false,
    projects: [],
    projectsLoading: false,
    finishSuccess: false,
    uploadSuccess: false,
    online: true,
    nextAdHocCommunityId: getRandomNegativeIdForAdHocComm([]),
  },
  actions: {
    async fetchConfiguration({ commit, state }) {
      if (!state.configLoaded && !state.configLoading) {
        commit('SET_LOADING', true);
        const configStore = createStore('fieldToolConfig', 'config');
        await axios.get('/config.json')
          .then((response) => {
            commit('SET_CONFIGURATION', response.data);
            set('communityAssessorUrl', response.data.communityAssessorUrl, configStore);
            set('wrapUrl', response.data.wrapUrl, configStore);
            commit('SET_LOADING', false);
            commit('SET_LOADED');
          }).catch(async () => {
            commit('SET_CONFIGURATION', {
              communityAssessorUrl:
              await get('communityAssessorUrl', configStore),
              wrapUrl:
              await get('wrapUrl', configStore),
            });
            commit('SET_LOADING', false);
            commit('SET_LOADED');
          });
      }
    },
    async setNewTokenAndLoggedInStatus({ commit }, { newToken, userLoggedIn }) {
      const accessStore = createStore('fieldToolAccess', 'access');
      set('access_token', newToken, accessStore);
      set('userLoggedIn', userLoggedIn, accessStore);
      commit('SET_LOGGED_IN', true);
      commit('SET_USER_LOGGED_IN', userLoggedIn);
    },
    async refreshLoggedInStatus({ commit }) {
      const token = await getJwt();
      let expirationTime = null;
      let userLoggedIn = '';

      if (token) {
        // extract token's expiration time
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        /* eslint-disable prefer-template */
        const jsonPayload = JSON.parse(decodeURIComponent(atob(base64).split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')));

        expirationTime = new Date(jsonPayload.exp * 1000);
        // extract token's user name
        userLoggedIn = jsonPayload.unique_name;
      }

      if (!token) {
        commit('SET_LOGGED_IN', false);
        commit('SET_USER_LOGGED_IN', '');
      } else if (!expirationTime || expirationTime < Date.now()) {
        // we do not want to sign out the user if offline
        // so we'll keep the user signed in FTM until we check network status
        // but we flag loggedIn as false to indicate this situation
        commit('SET_LOGGED_IN', false);
        commit('SET_USER_LOGGED_IN', userLoggedIn);
      } else {
        commit('SET_LOGGED_IN', true);
        commit('SET_USER_LOGGED_IN', userLoggedIn);
      }
    },
    async LogOut({ commit }) {
      // go back to default/initial state
      // but do not remove this on sign out so the forgot password link still works
      // commit('SET_CONFIGURATION', {});
      commit('SET_LOADING', false);
      commit('SET_LOADED', false);
      commit('SET_LOGGED_IN', false);
      commit('SET_USER_LOGGED_IN', '');
      commit('SET_ASSESSMENT_CATEGORIES', []);
      commit('SET_CATEGORIES_LOADING', false);
      commit('SET_FIRE_DEPARTMENTS', []);
      commit('SET_FIRE_DEPARTMENTS_LOADING', false);
      commit('SET_CURRENT_COMMUNITY', null);
      commit('SET_DOWNLOADED_COMMUNITIES', []);
      commit('SET_COMMUNITIES', []);
      commit('SET_COMMUNITIES_LOADING', false);
      commit('SET_FINISH_SUCCESS', false);
      commit('SET_UPLOADING', false);
    },
    async fetchAssessmentCategories({ commit, state }) {
      const categoriesStore = createStore('fieldToolCategories', 'assessmentCategories');

      if (state.loggedIn && navigator.onLine) {
        if (!state.assessmentCategories || state.assessmentCategories.length === 0) {
          commit('SET_CATEGORIES_LOADING', true);
          const config = {
            headers: { Authorization: `Bearer ${await getJwt()}` },
          };
          await axios.get(`${state.config.communityAssessorUrl}/categories`, config)
            .then((response) => {
              if (response && response.data && response.data.accessToken) {
                const accessStore = createStore('fieldToolAccess', 'access');
                set('access_token', response.data.accessToken, accessStore);
              }
              const categories = response.data.items;
              // deal with sorting sub collections of classes and conditions
              categories.map((cat) => {
                const newCat = cat;
                newCat.HazardClasses = cat.HazardClasses.sort((a, b) => {
                  if (a.Row === b.Row) {
                    return a.Col - b.Col;
                  }
                  return a.Row - b.Row;
                }).map((hazClass) => {
                  const newClass = hazClass;
                  newClass.HazardConditions = hazClass.HazardConditions
                    .sort((a, b) => a.DisplayOrder - b.DisplayOrder);
                  return newClass;
                });
                return newCat;
              });
              commit('SET_ASSESSMENT_CATEGORIES', categories);
              setMany(categories.map((ac) => [ac.HazardCategoryId, ac]), categoriesStore);
            })
            .catch((exc) => {
              console.log(`error: ${exc}`);
              values(categoriesStore).then((acEntries) => {
                commit('SET_ASSESSMENT_CATEGORIES', acEntries);
              });

              if (exc.message === 'Request failed with status code 401') {
                commit('SET_LOGGED_IN', false);
              }
            })
            .finally(() => {
              commit('SET_CATEGORIES_LOADING', false);
            });
        }
      } else if (!navigator.onLine) {
        commit('SET_CATEGORIES_LOADING', true);
        values(categoriesStore).then((acEntries) => {
          commit('SET_ASSESSMENT_CATEGORIES', acEntries);
        });
        commit('SET_CATEGORIES_LOADING', false);
      }
    },
    async fetchFireDepartments({ commit, state }) {
      const fdStore = createStore('fieldToolFireDepartments', 'fireDepartments');
      if (state.loggedIn) {
        if (!state.fireDepartments || state.fireDepartments.length === 0) {
          commit('SET_FIRE_DEPARTMENTS_LOADING', true);
          const config = {
            headers: { Authorization: `Bearer ${await getJwt()}` },
          };
          await axios.get(`${state.config.communityAssessorUrl}/fireDepartments`, config)
            .then((response) => {
              if (response && response.data && response.data.accessToken) {
                const accessStore = createStore('fieldToolAccess', 'access');
                set('access_token', response.data.accessToken, accessStore);
              }
              const items = response.data.items.map((x) => ({ id: x.Key, fdName: x.Value }));
              commit('SET_FIRE_DEPARTMENTS', items);
              setMany(items.map((fd) => [fd.id, fd]), fdStore);
            })
            .catch((exc) => {
              console.log(`error: ${exc}`);
              values(fdStore).then((fdEntries) => {
                commit('SET_FIRE_DEPARTMENTS', fdEntries);
              });
              if (exc.message === 'Request failed with status code 401') {
                commit('SET_LOGGED_IN', false);
              }
            })
            .finally(() => {
              commit('SET_FIRE_DEPARTMENTS_LOADING', false);
            });
        }
      } else if (!navigator.onLine) {
        commit('SET_FIRE_DEPARTMENTS_LOADING', true);
        values(fdStore).then((fdEntries) => {
          commit('SET_FIRE_DEPARTMENTS', fdEntries);
        });
        commit('SET_FIRE_DEPARTMENTS_LOADING', false);
      }
    },
    async updateCurrentCommunity({ commit }, community) {
      // save to idb
      const communitiesStore = createStore('fieldToolCommunities', 'communities');
      set(community.CommunityId, community, communitiesStore);
      // and update the downloadedCommunities and the currentCommunity
      commit('SET_CURRENT_COMMUNITY', community);
      commit('SET_DOWNLOADED_COMMUNITIES', await entries(communitiesStore));
    },
    async setFinishSuccess({ commit }) {
      commit('SET_FINISH_SUCCESS', true);
    },
    async setUploadSuccess({ commit }) {
      commit('SET_UPLOAD_SUCCESS', true);
    },
    async resetFinishSuccess({ commit }) {
      commit('SET_FINISH_SUCCESS', false);
    },
    async resetUploadSuccess({ commit }) {
      commit('SET_UPLOAD_SUCCESS', false);
    },
    async setCurrentCommunityById({ commit }, id) {
      const communitiesStore = createStore('fieldToolCommunities', 'communities');
      const comm = await get(id, communitiesStore);
      commit('SET_CURRENT_COMMUNITY', comm);
    },
    async fetchProjects({ commit, state }) {
      const projectsStore = createStore('fieldToolProjects', 'projects');
      commit('SET_PROJECTS', await entries(projectsStore));

      if (state.loggedIn) {
        commit('SET_PROJECTS_LOADING', true);
        const config = {
          headers: { Authorization: `Bearer ${await getJwt()}` },
        };
        // get all of the user authorized projects from the server
        await axios.get(`${state.config.communityAssessorUrl}/myProjectsWithToken`, config)
          .then((response) => {
            if (response && response.data && response.data.accessToken) {
              const accessStore = createStore('fieldToolAccess', 'access');
              set('access_token', response.data.accessToken, accessStore);
            }
            const items = response.data.items.map((x) => ({
              value: x,
              text: x.ProjectName,
              projectId: x.ProjectId,
            }));
            commit('SET_PROJECTS', items);
            setMany(items.map((p) => [p.projectId, p]), projectsStore);
          })
          .catch((exc) => {
            console.log(`error: ${exc}`);
            values(projectsStore).then((projectEntries) => {
              commit('SET_PROJECTS', projectEntries);
            });
            if (exc.message === 'Request failed with status code 401') {
              commit('SET_LOGGED_IN', false);
              commit('SET_USER_LOGGED_IN', '');
            }
          })
          .finally(() => {
            commit('SET_PROJECTS_LOADING', false);
          });
      }
    },
    async fetchAssessmentModel({ commit, state }) {
      const assessmentModelStore = createStore('fieldToolAssessModel', 'assessment_model');
      commit('SET_ASSESSMENT_MODEL', await entries(assessmentModelStore));

      if (state.loggedIn) {
        const config = {
          headers: { Authorization: `Bearer ${await getJwt()}` },
        };
        await axios.get(`${state.config.communityAssessorUrl}/GetEmptyAssessment`, config)
          .then((response) => {
            if (response && response.data && response.data.accessToken) {
              const accessStore = createStore('fieldToolAccess', 'access');
              set('access_token', response.data.accessToken, accessStore);
            }
            const model = response.data.items;
            commit('SET_ASSESSMENT_MODEL', model);
            set('assessment_model', model, assessmentModelStore);
          })
          .catch((exc) => {
            console.log(`error: ${exc}`);
            values(assessmentModelStore).then((assessmentModel) => {
              commit('SET_ASSESSMENT_MODEL', assessmentModel[0]);
            });
            if (exc.message === 'Request failed with status code 401') {
              commit('SET_LOGGED_IN', false);
              commit('SET_USER_LOGGED_IN', '');
            }
          });
      }
    },
    async fetchCommunities({ commit, state }) {
      const communitiesStore = createStore('fieldToolCommunities', 'communities');
      commit('SET_DOWNLOADED_COMMUNITIES', await entries(communitiesStore));

      if (state.loggedIn) {
        commit('SET_COMMUNITIES_LOADING', true);
        const config = {
          headers: { Authorization: `Bearer ${await getJwt()}` },
        };
        // get all of the unassessed communities from the server
        await axios.get(`${state.config.communityAssessorUrl}/myUnassessedCommunities`, config)
          .then((response) => {
            if (response && response.data && response.data.accessToken) {
              const accessStore = createStore('fieldToolAccess', 'access');
              set('access_token', response.data.accessToken, accessStore);
            }

            commit('SET_COMMUNITIES', response.data.items);
          })
          .catch((exc) => {
            console.log(`error: ${exc}`);
            commit('SET_COMMUNITIES', []);
            if (exc.message === 'Request failed with status code 401') {
              commit('SET_LOGGED_IN', false);
              commit('SET_USER_LOGGED_IN', '');
            }
          })
          .finally(() => {
            commit('SET_COMMUNITIES_LOADING', false);
          });
      }
    },
    async downloadCommunity({ state, commit }, id) {
      const selectedCommunity = state.communities.find((comm) => comm.CommunityId === id);
      selectedCommunity.status = 'downloaded';
      const communitiesStore = createStore('fieldToolCommunities', 'communities');
      set(selectedCommunity.CommunityId, selectedCommunity, communitiesStore);
      commit('ADD_TO_DOWNLOADED_COMMUNITIES', [selectedCommunity.CommunityId, selectedCommunity]);
    },
    async addAdHocToCommunities({ commit }, adHocCommunity) {
      const communitiesStore = createStore('fieldToolCommunities', 'communities');
      set(adHocCommunity.CommunityId, adHocCommunity, communitiesStore);
      commit('ADD_TO_COMMUNITIES', [adHocCommunity.CommunityId, adHocCommunity]);
    },
    async setNextAdHocCommunityId({ commit, state }) {
      const nextId = getRandomNegativeIdForAdHocComm(state.communities.map((c) => c.CommunityId));
      commit('SET_NEXT_AD_HOC_COMMUNITY_ID', nextId);
    },
    async deleteDownloadedCommunity({ commit }, communityId) {
      const communitiesStore = createStore('fieldToolCommunities', 'communities');

      // remove from indexedDb then reset the downloadedCommunities in state
      del(communityId, communitiesStore);
      commit('SET_DOWNLOADED_COMMUNITIES', await entries(communitiesStore));
    },
    async uploadPhotos({ state, commit }, community) {
      if (state.loggedIn) {
        const config = {
          headers: {
            Authorization: `Bearer ${await getJwt()}`,
            'Content-Type': 'multipart/form-data',
          },
        };
        const communityToUpdate = community;
        const photoFiles = new FormData();
        /* eslint-disable no-plusplus */
        for (let imageIndexNumber = 1; imageIndexNumber <= 3; imageIndexNumber++) {
          const indexName = `Image${imageIndexNumber}File`;
          if (community[indexName] && community[indexName].size > 0) {
            photoFiles.append(`photoFiles[${imageIndexNumber - 1}]`, community[indexName]);
          }
        }

        return axios
          .post(`${state.config.communityAssessorUrl}/UploadPhotos`, photoFiles, config)
          .then(async (response) => {
            communityToUpdate.AssessmentPictureIds = response.data.pictureIds;
            // save to idb
            const communitiesStore = createStore('fieldToolCommunities', 'communities');
            set(communityToUpdate.CommunityId, communityToUpdate, communitiesStore);
            // and update the downloadedCommunities and the currentCommunity
            commit('SET_CURRENT_COMMUNITY', communityToUpdate);
            commit('SET_DOWNLOADED_COMMUNITIES', await entries(communitiesStore));
          }).catch((exc) => {
            console.log(`error: ${exc}`);
            if (exc.message === 'Request failed with status code 401') {
              commit('SET_LOGGED_IN', false);
            }
            return Promise.reject(exc.response);
          });
      }
      return new Promise(() => {}).reject(new Error('Not logged in.'));
    },
    async uploadCommunity({ commit, state }, community) {
      const commStore = createStore('fieldToolCommunities', 'communities');

      if (state.loggedIn) {
        const config = {
          headers: { Authorization: `Bearer ${await getJwt()}` },
        };
        /* eslint-disable no-param-reassign */
        if (!community.FireProtectionDistrictId) {
          community.FireProtectionDistrictId = community.FireDepartmentId;
        }
        /* eslint-enable no-param-reassign */
        // To look if I have to use the Sync endpoint (for ad hoc comms)
        // or the Assessment Create endpoint
        const communityId = parseInt(community.CommunityId, 10);

        if (communityId && communityId > 0) {
          // post the assessment to the server
          return axios.post(`${state.config.communityAssessorUrl}/Project/${community
            .ProjectId}/Community/${community.CommunityId}/Assessment/Create`, community, config)
            .then(async (response) => {
              if (response && response.data && response.data.accessToken) {
                const accessStore = createStore('fieldToolAccess', 'access');
                set('access_token', response.data.accessToken, accessStore);
              }

              // remove from indexedDb then reset the downloadedCommunities in state
              del(community.CommunityId, commStore);
              commit('REMOVE_AVAILABLE_COMMUNITY', community.CommunityId);
              commit('SET_DOWNLOADED_COMMUNITIES', await entries(commStore));
            })
            .catch((exc) => {
              console.log(`error: ${exc}`);
              return Promise.reject(exc.response);
            });
        }
        // If we reached this, community id is negative so it's an ad hoc
        // sync up the ad hoc to the server
        const syncModel = {
          Community: community,
          Assessment: community,
        };
        return axios.post(`${state.config.communityAssessorUrl}/Project/${community
          .ProjectId}/Sync`, syncModel, config)
          .then(async (response) => {
            if (response && response.data && response.data.accessToken) {
              const accessStore = createStore('fieldToolAccess', 'access');
              set('access_token', response.data.accessToken, accessStore);
            }

            // remove from indexedDb
            del(community.CommunityId, commStore);
            commit('REMOVE_AVAILABLE_COMMUNITY', community.CommunityId);
            // set next Ad Hoc Community Id
            const nextId = getRandomNegativeIdForAdHocComm(state.communities
              .map((c) => c.CommunityId));
            commit('SET_NEXT_AD_HOC_COMMUNITY_ID', nextId);
            // and reset the downloadedCommunities in state
            commit('SET_DOWNLOADED_COMMUNITIES', await entries(commStore));
          })
          .catch((exc) => {
            console.log(`error: ${exc}`);
            return Promise.reject(exc.response);
          });
      }
      return new Promise(() => {}).reject(new Error('Not logged in.'));
    },
    async setOnline({ commit }) {
      commit('SET_ONLINE', true);
    },
    async setOffline({ commit }) {
      commit('SET_ONLINE', false);
    },
    async setUploading({ commit }, uploading) {
      commit('SET_UPLOADING', uploading);
    },
  },
  mutations: {
    ADD_TO_DOWNLOADED_COMMUNITIES(state, community) {
      state.downloadedCommunities.push(community);
    },
    ADD_TO_COMMUNITIES(state, adHocCommunity) {
      state.communities.push(adHocCommunity);
    },
    SET_LOGGED_IN(state, loggedIn) {
      state.loggedIn = loggedIn;
    },
    SET_ONLINE(state, online) {
      state.online = online;
    },
    SET_USER_LOGGED_IN(state, username) {
      state.userLoggedIn = username;
    },
    SET_CONFIGURATION(state, config) {
      state.config = config;
    },
    SET_LOADING(state, loading) {
      state.configLoading = loading;
    },
    SET_UPLOADING(state, uploading) {
      state.uploading = uploading;
    },
    SET_CATEGORIES_LOADING(state, loading) {
      state.categoriesLoading = loading;
    },
    SET_LOADED(state) {
      state.configLoaded = true;
    },
    SET_ASSESSMENT_CATEGORIES(state, categories) {
      state.assessmentCategories = categories;
    },
    SET_FIRE_DEPARTMENTS(state, fds) {
      state.fireDepartments = fds;
    },
    SET_FIRE_DEPARTMENTS_LOADING(state, fdsLoading) {
      state.fireDepartmentsLoading = fdsLoading;
    },
    SET_CURRENT_COMMUNITY(state, community) {
      state.currentCommunity = community;
    },
    SET_COMMUNITIES(state, communities) {
      state.communities = communities;
    },
    REMOVE_AVAILABLE_COMMUNITY(state, communityId) {
      const i = state.communities.map((item) => item.CommunityId).indexOf(communityId);
      if (i >= 0) {
        state.communities.splice(i, 1);
      }
    },
    SET_DOWNLOADED_COMMUNITIES(state, communities) {
      state.downloadedCommunities = communities;
    },
    SET_COMMUNITIES_LOADING(state, loading) {
      state.communitiesLoading = loading;
    },
    SET_PROJECTS(state, projects) {
      state.projects = projects;
    },
    SET_ASSESSMENT_MODEL(state, model) {
      state.assessmentModel = model;
    },
    SET_PROJECTS_LOADING(state, loading) {
      state.projectsLoading = loading;
    },
    SET_FINISH_SUCCESS(state, success) {
      state.finishSuccess = success;
    },
    SET_UPLOAD_SUCCESS(state, success) {
      state.uploadSuccess = success;
    },
    SET_NEXT_AD_HOC_COMMUNITY_ID(state, nextId) {
      state.nextAdHocCommunityId = nextId;
    },
  },
  getters: {
    numericFields(state) {
      if (state.assessmentCategories.length === 0) {
        return [];
      }
      return state.assessmentCategories.flatMap((ac) => ac.HazardClasses
        .filter((hc) => hc.Type === 'text')
        .map((hc) => hc.Name));
    },
    requiredFieldsCountObj(state) {
      if (state.assessmentCategories.length === 0) {
        return [];
      }
      return state.assessmentCategories
        .map((cat) => ({
          id: cat.HazardCategoryId,
          name: cat.DisplayName,
          requiredCount: cat.HazardClasses.filter((hc) => hc.IsRequired).length,
          completedRequiredCount: cat.HazardClasses.filter((hc) => hc.IsRequired
            && state.currentCommunity[hc.Name]).length,
        }));
    },
    incompleteCategoriesObj(state) {
      if (state.assessmentCategories.length === 0) {
        return [];
      }
      return state.assessmentCategories
        .map((cat) => ({
          id: cat.HazardCategoryId,
          name: cat.DisplayName,
          requiredCount: cat.HazardClasses.filter((hc) => hc.IsRequired).length,
          completedRequiredCount: cat.HazardClasses.filter((hc) => hc.IsRequired
            && state.currentCommunity[hc.Name]).length,
        }))
        .filter((item) => item.requiredCount > item.completedRequiredCount)
        .map((item) => item.name);
    },
    availableCommunities(state) {
      // get all communities that are available, filter out any that have been downloaded
      // and add a status of 'available'
      let availableCommunities = [];
      if (navigator.onLine) {
        availableCommunities = state.communities
          .filter((item) => state.downloadedCommunities
            .findIndex((entry) => entry[1].CommunityId === item.CommunityId) < 0)
          .map((obj) => ({ ...obj, status: 'available' }));
      }

      // set up a function that sets up the secondary sort on CommunityName
      const compareByCommunityName = (a, b) => (a.CommunityName || '').toUpperCase()
        .localeCompare((b.CommunityName || '').toUpperCase());

      // set up a function to get only downloadedCommunities by a specific status
      // and sort them by CommunityName
      const downloadedCommunitiesByStatus = (status) => state.downloadedCommunities
        .map((entry) => entry[1])
        .filter((comm) => comm.status === status)
        .sort((a, b) => compareByCommunityName(a, b));

      // combine downloaded communities with ones available for download
      // this is done to force the order of displayed communities by status
      // and by CommunityName within each status
      return downloadedCommunitiesByStatus('started')
        .concat(downloadedCommunitiesByStatus('downloaded'))
        .concat(downloadedCommunitiesByStatus('finished'))
        .concat(availableCommunities.sort((a, b) => compareByCommunityName(a, b)));
    },
  },
  modules: {
  },
});
